import styled from "styled-components";
import { memo, useState } from "react";
import { router, Head } from "@inertiajs/react";

import { theme } from "../_kit/theme";
import PageShell from "../_kit/PageShell";
import { formatDateTime, wibTodayIso } from "../_kit/dateFormat";
import { useT } from "../_kit/i18n";

const Header = styled.div`
  margin-bottom: 1rem;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.4rem;
`;

const Subtitle = styled.p`
  margin: 0.25rem 0 0;
  color: ${theme.muted};
  font-size: 0.9rem;
`;

const Composer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1rem;
  background: #ffffff;
  border: 1px solid ${theme.border};
  border-radius: 12px;
  margin-bottom: 1.25rem;
`;

const AuthorField = styled.input`
  padding: 0.5rem 0.7rem;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  font-size: 0.9rem;
  max-width: 260px;
`;

const BodyField = styled.textarea`
  padding: 0.5rem 0.7rem;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  font-size: 0.95rem;
  min-height: 80px;
  resize: vertical;
`;

const AddButton = styled.button`
  align-self: flex-start;
  padding: 0.55rem 1.1rem;
  border: 0;
  border-radius: 9px;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
  color: #ffffff;
  background: ${theme.ink};
  &:hover {
    background: ${theme.inkSoft};
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const NoteList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const Note = styled.div`
  padding: 1rem 1.15rem;
  background: #ffffff;
  border: 1px solid ${theme.border};
  border-left: 4px solid ${theme.gold};
  border-radius: 10px;
`;

const NoteMeta = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
`;

const Author = styled.span`
  font-size: 1.05rem;
  font-weight: 800;
  color: ${theme.ink};
`;

const NoteTime = styled.span`
  font-size: 0.85rem;
  color: ${theme.muted};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const Body = styled.div`
  white-space: pre-wrap;
  font-size: 1rem;
  line-height: 1.6;
  color: ${theme.ink700};
`;

const Empty = styled.div`
  padding: 2rem 1rem;
  text-align: center;
  color: ${theme.muted};
  background: #ffffff;
  border: 1px dashed ${theme.border};
  border-radius: 12px;
`;

const Pager = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 1rem;
`;

const PageButton = styled.button`
  padding: 0.4rem 0.9rem;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  background: #ffffff;
  font-size: 0.85rem;
  font-weight: 700;
  color: ${theme.ink};
  cursor: pointer;
  &:hover:not(:disabled) {
    background: ${theme.goldSoft};
    border-color: ${theme.gold};
  }
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

const PageInfo = styled.span`
  font-size: 0.85rem;
  color: ${theme.muted};
  font-variant-numeric: tabular-nums;
`;

const readAuthor = () => {
  if (typeof localStorage === "undefined") {
    return "";
  }
  return localStorage.getItem("frontdesk_acting_name") || "";
};

// The composer owns its own author/body state so typing a long handover paragraph only re-renders
// this small form — never the notes list below it.
const NoteComposer = memo((props) => {
  const { t } = useT();
  const [author, setAuthor] = useState(readAuthor);
  const [body, setBody] = useState("");

  const submit = () => {
    if (!body.trim()) {
      return;
    }
    if (typeof localStorage !== "undefined") {
      localStorage.setItem("frontdesk_acting_name", author);
    }
    router.post(
      "/frontdesk_v2/shift_notes",
      { author_name: author, body },
      { preserveScroll: true, onSuccess: () => setBody("") },
    );
  };

  return (
    <Composer>
      <AuthorField
        type="text"
        placeholder={t("note_author")}
        value={author}
        onChange={(event) => setAuthor(event.target.value)}
      />
      <BodyField
        placeholder={t("note_body")}
        value={body}
        onChange={(event) => setBody(event.target.value)}
      />
      <AddButton type="button" disabled={!body.trim()} onClick={submit}>
        {t("add_note")}
      </AddButton>
    </Composer>
  );
});
NoteComposer.displayName = "NoteComposer";

const ShiftNotesIndex = (props) => {
  const { t, lang } = useT();
  const notes = props.notes || [];
  const pagination = props.pagination || {
    page: 1,
    total_pages: 1,
    total_count: notes.length,
  };
  // Page within the current two-day window. Keep the active date (unless it is today, the server
  // default) so paging never drops the shared filter. preserveState keeps the composer's draft.
  const goPage = (page) => {
    const params = {};
    if (props.date && props.date !== wibTodayIso()) {
      params.date = props.date;
    }
    if (page > 1) {
      params.page = page;
    }
    router.get("/frontdesk_v2/shift_notes", params, {
      preserveScroll: true,
      preserveState: true,
    });
  };

  return (
    <PageShell>
      <Head title={t("notes_title")} />
      <Header>
        <Title>{t("notes_title")}</Title>
        <Subtitle>{t("notes_subtitle")}</Subtitle>
      </Header>

      <NoteComposer />

      {notes.length === 0 ? (
        <Empty>{t("notes_empty")}</Empty>
      ) : (
        <>
          <NoteList>
            {notes.map((note) => (
              <Note key={note.id}>
                <NoteMeta>
                  <Author>{note.author_name || "—"}</Author>
                  <NoteTime>{formatDateTime(note.created_at, lang)}</NoteTime>
                </NoteMeta>
                <Body>{note.body}</Body>
              </Note>
            ))}
          </NoteList>
          {pagination.total_pages > 1 ? (
            <Pager>
              <PageButton
                type="button"
                disabled={pagination.page <= 1}
                onClick={() => goPage(pagination.page - 1)}
              >
                ← {t("notes_prev")}
              </PageButton>
              <PageInfo>
                {t("notes_page")} {pagination.page} / {pagination.total_pages} (
                {pagination.total_count})
              </PageInfo>
              <PageButton
                type="button"
                disabled={pagination.page >= pagination.total_pages}
                onClick={() => goPage(pagination.page + 1)}
              >
                {t("notes_next")} →
              </PageButton>
            </Pager>
          ) : null}
        </>
      )}
    </PageShell>
  );
};

export default ShiftNotesIndex;
